
<template lang="pug">
drawer(:dismissRoute="dismissRoute")
  .drawer-title
    i.mat-icon add_circle
    | {{this.requestTitle}}

  el-autocomplete(v-if="!customerId"
                  v-model="currentCustomer"
                  :fetch-suggestions="searchCustomer"
                  class="new-request__search"
                  prefix-icon="el-icon-search"
                  placeholder="Please, input customer name"
                  @select="handleSearch")

  el-form(
    v-else
    :label-width='$labelWidth', label-position='right', ref='requestForm'
    v-loading='loadingForm', :model='request', :rules='rules'
    )

    el-form-item(label='Event name', prop='name')
      el-input(v-model='request.name' placeholder='Annual board meeting')

    // PEOPLE
    el-form-item(label='Number of people', prop='people')
      el-input-number(v-model='request.people', :min='1')

    // BUDGET
    el-form-item(label='Budget per person', prop='budget')
      el-input(v-model='request.budget')
        template(slot='prepend')
          | $

    // CUISINES
    el-form-item(label='Preferred cuisines', prop='cuisine_ids')
      el-select(v-model='request.cuisine_ids', multiple='', placeholder='Select up to 3', :multipleLimit='3', class='full-width-select')
        el-option(v-for='item in cuisines', :key='item.id', :label='item.name', :value='item.id')

    // ADDRESS
    el-form-item(label='Delivery address', prop='address_id')
      el-col(:span="17")
        el-select(v-model='request.address_id',  placeholder='Select an option')
          el-option(
            v-for='item in addresses', :key='item.id',
            :label='item.address_line_1', :value='item.id'
          )
          el-option(
            label='Create new address', value='new', class='select-special-action'
          )
      el-col(:span="7")
        el-form-item(v-show='request.address_id && !creatingAddress' prop='suite')
          el-input(v-model='request.suite'  placeholder='Suite')
          
    section-separator(v-show='creatingAddress', label='New address information')
      address-builder(ref='addressForm', label-position='right', :label-width='$labelWidth', newRequest='true', :customerId='customerId')

    // DATE
    el-form-item(label='Delivery date', prop='delivery_date')
      el-date-picker(
        v-model='request.delivery_date', placeholder='Select a date',
        :pickerOptions='datePickerOptions', value-format='yyyy-MM-dd'
      )

    // TIME
    el-form-item(label='Delivery time', prop='delivery_time')
      el-time-picker(type='date'
        placeholder='Select a time', v-model='request.delivery_time',
        :picker-options="timePickerOptions",
        format="hh:mm A",
        value-format="HH:mm"
      )
    //- pre {{ request.delivery_time }}

    // CONTACT
    el-form-item(label='Contact for this request', prop='contact_id')
      el-select(v-model='request.contact_id', placeholder='Select an option')
        el-option(
          v-for='item in contacts', :key='item.id',
          :label='item.first_name + " " + item.last_name', :value='item.id'
          )
        el-option(
          label='Create new contact', value='new', class='select-special-action'
        )
    section-separator(v-show='creatingContact', label='New contact information')
      contact-builder(ref='contactForm', label-position='right', :label-width='$labelWidth', :customerId='customerId')

    // NOTES
    el-form-item(label='Custom customer PO', prop='customerPO')
      el-input(v-model='order.customer_po')

    el-form-item(label='Anything we should know?', prop='notes')
      el-input(
        type='textarea' v-model='request.notes' max='250',
        :autosize='{ minRows: 2, maxRows: 7 }'
      )

    el-collapse
      el-collapse-item(name='preferences')
        template(slot='title')
          el-form-item(label='Preferences')
            span.collapse-item__tip Click to expand
        el-form-item(prop='preferences')
          preferences-builder(
            :initialValue='preferences',
            ref='preferencesForm',
            :is-request='true'
          )


  // BUTTONS
  div.buttons-section
    el-button(v-if='customerId' type='primary' @click='onSubmit', :loading='submitting')
      | Save
    el-button(@click='onCancel')
      | Cancel
</template>

<script>
  import ContactBuilder from '../../user/blocks/ContactBuilder'
  import AddressBuilder from '../../common/AddressBuilder'
  import PreferencesBuilder from '../../user/blocks/PreferencesBuilder'

  function data(){
    return {
      isStaff: this.$route.matched[0].path === '/staff',
      requestTitle: 'New Request',
      dismissRoute: this.$route.matched[1].path || '',
      rules: buildFormRules(this),
      skipNestedFormsValidation: true,
      loadingForm: true,
      displayContactForm: false,
      displayAddressForm: false,
      cuisines: [],
      contacts: [],
      addresses: [],
      order: {},
      dataForSubmit: null,
      submitting: false,
      request: emptyRequest(),
      preferences: {},
      isContactValid: false,
      datePickerOptions: {
        disabledDate(time) {
          const today = new Date()
          const yesterday = new Date()
          yesterday.setDate(today.getDate() - 1)
          return time < yesterday
        }
      },
      timePickerOptions: {
        selectableRange: '07:00:00 - 19:30:00',
      },
      currentCustomer: '',
      customerId: null,
      customers: [],
      timeout:  null
    }
  }

  function emptyRequest ( ) {
    return {
      notes: '',
      cuisine_ids: [],
      people: null,
      budget: null,
      contact_id: null,
      address_id: null,
      delivery_date: null,
      delivery_time: null,
      suite: null
    }
  }

  function buildFormRules(componentInstance){
    return {
      name: {
        required: true, message: "Please enter a name for your request", trigger: 'blur'
      },
      notes: {
        max: 250, message: 'Notes cannot exceed 250 characters', trigger: 'change' },
      cuisine_ids: {
        type: 'array', required: true, trigger: 'change',
        message: 'Please select at least one cuisine' },
      people: {
        required: true, type: 'integer', message: 'Please input the number of people',
        trigger: 'blur' },
      budget: {
        required: true, type: 'number',
        message: 'Please input a valid budget per person in dollars',
        trigger: 'blur',
        transform: parseFloat },
      contact_id: [
        { validator: validateContact.bind(componentInstance), trigger: 'change' },
        { required: true, trigger: 'change'}
      ],
      address_id: [
        { validator: validateAddress.bind(componentInstance), trigger: 'change' },
        { required: true, trigger: 'change'}
      ],
      delivery_date: {
        required: true, message: 'Please pick a date', trigger: 'change' },
      delivery_time: {
        required: true, message: 'Please pick a time', trigger: 'change',
        pattern: /\d{2}:\d{2}/
      }
    }
  }

  function validateContact(rule, value, callback){
    if (value === null) return callback(new Error('Please select an option'))
    if (value === 'new'){
      if (this.skipNestedFormsValidation) return callback()
      this.$refs.contactForm.validate(callback)
    }else{
      if (Number.isInteger(value)){
        callback()
      }else{
        callback(new Error('Please give a valid id'))
      }
    }
  }


  function validateAddress(rule, value, callback){
    if (value === null) return callback(new Error('Please select an option'))
    if (value === 'new'){
      if (this.skipNestedFormsValidation) return callback()
      this.$refs.addressForm.validate(callback)
    }else{
      if (Number.isInteger(value)){
        callback()
      }else{
        callback(new Error('Please give a valid id'))
      }
    }
  }

  function getStaffData() {
    this.loadingForm = true;
    Promise.all([
      this.getAddresses(this.customerId),
      this.getContacts(this.customerId),
      this.getCuisines(),
      this.getPreferences(this.customerId),
    ]).then(()=>{
      this.loadingForm = false
    })
  }

  function searchCustomer(queryString, cb) {
    if (queryString) {
      const request = `/customers?account_name=${queryString}`;
      this.getCustomers(request).then(r => {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          cb(this.customers);
        }, 500);
      })
    }
  }

  function handleSearch(item) {
    this.customerId = item.link;
    this.getStaffData();
  }

  function getCustomers(filterName) {
    return this.$simplecater.get(filterName).then((r)=>{
      this.customers = this.generateSearchArray(r.data.data);
    });
  }

  function generateSearchArray(array) {
    return array.map(item => ({value: item.account_name, link: item.id}))
  }

  function getCuisines(){
    return this.$simplecater.get('/cuisines').then((r)=> {
      this.cuisines = r.data.data;
    })
  }

  function getAddresses(id){
    return this.$simplecater.get(`/customers/${id}/addresses`).then((r)=>{
      this.addresses = r.data.data;
    })
  }

  function getPreferences(id){
    this.$simplecater.get(`/customers/${id}/preferences`).then((r)=> {
      this.preferences = r.data.data;
    })
  }

  function getContacts(id){
    return this.$simplecater.get(`/customers/${id}/contacts`).then((r)=>{
      this.contacts = r.data.data;
    })
  }

  function onCancel(){
    this.$router.push(this.dismissRoute)
  }

  function createContact(){
    return this.$refs.contactForm.create().then((newContact)=>{
      this.dataForSubmit.contact_id = newContact.id
    })
  }

  function createAddress(){
    return this.$refs.addressForm.create().then((newAddress)=>{
      this.dataForSubmit.address_id = newAddress.id
    })
  }

  function onSubmit(){
    this.skipNestedFormsValidation = false;
    this.$refs.requestForm.validate((valid) => {
      if (valid) {
        this.submitForm()
      } else {
        this.$message.error('Some validations failed, please check your inputs')
        return false;
      }
    })
  }

  function submitForm(){
    this.submitting = true;
    let promises = [];
    this.dataForSubmit = Object.assign({},this.request);
    this.dataForSubmit.preferences = Object.assign({},
      this.$refs.preferencesForm.preferences
    );
    this.dataForSubmit.budget_cents = this.request.budget * 100;
    this.dataForSubmit.delivery_date = this.dataForSubmit.delivery_date;
    if (this.order.customer_po) this.dataForSubmit.customer_po = this.order.customer_po;
    if (this.creatingContact) promises.push(this.createContact());
    if (this.creatingAddress) promises.push(this.createAddress());
    Promise.all(promises).then(()=>{
      const id = this.$route.params.id;
      const getRequestType = () =>{
        return this.$simplecater.post(`/customers/${this.customerId}/requests`, this.dataForSubmit);
      };
      getRequestType().then(() => {
        const message = 'Your request has been sent';
        this.$bus.$emit('request-created')
        this.$message(message)
        this.request = emptyRequest();
        this.onCancel();
        this.$nextTick(()=>{
          this.$router.push(this.dismissRoute)
        })
      }).catch(
        this.$handleErrorFor('Creating request').bind(this)
      ).finally(()=>{
        this.submitting = false
      })
    })
  }

  const props = { closeModal: { required: false } };

  const computed = {
    creatingAddress: function(){
      return this.request.address_id === 'new';
    },
    creatingContact: function(){
      return this.request.contact_id === 'new';
    }
  };

  const components = { ContactBuilder, AddressBuilder, PreferencesBuilder };

  const methods = {
    getContacts, getAddresses, getCuisines, getPreferences, submitForm,
    onCancel, onSubmit, createContact, validateContact, validateAddress,
    createAddress, getStaffData,
    searchCustomer, handleSearch, getCustomers, generateSearchArray
  };

  export default { data, components, methods, computed }
</script>


<style scoped lang="sass">
  @import '../../variables'

  .collapse-item__tip
    font-style: italic
    opacity: 0.8
    display: inline-block
    float: right
    margin-right: 1rem
    font-weight: 600
    color: $colorPrimary
    &:hover
      opacity: 1

  .drawer .el-collapse-item__header, .drawer .el-collapse-item__wrap
    background-color: #FAFAFA

  .new-request__search-cont
    padding: 0 0 30px

  .new-request__search
    width: 100%
  .buttons-section
    margin-bottom: 20px
    text-align: center
</style>



// WEBPACK FOOTER //
// src/staff/blocks/NewRequest.vue